import { Flex, Card } from '@chakra-ui/react';
import { INoteReqSchema, INoteResSchema, IUser } from '@monetize/types/app';
import truncate from 'lodash/truncate';
import { useState } from 'react';
import { MdMoreVert } from 'react-icons/md';
import { handleApiErrorToast } from '../../api/axios';
import { useShowMore } from '../../hooks/useShowMore';
import { useAuth } from '../../services/auth0';
import {
  MBox,
  MButton,
  MCard,
  MDropdownActionItem,
  MDropdownActions,
  MFlex,
  MIconAction,
  MText,
} from '../Monetize';
import { DateDisplay } from '../Monetize/DateDisplay';
import { DeleteConfirmCard } from './DeleteConfirmCard';
import { NoteForm } from './NoteForm';

interface NoteCardProps {
  note: INoteResSchema;
  unsavedNotes?: Map<string, INoteResSchema>;
  handleNoteModified: (action: 'ADD' | 'CANCEL', note: INoteResSchema) => void;
  createNote: (data: INoteReqSchema) => void;
  updateNote: (note: INoteReqSchema) => void;
  deleteNote: (note: INoteReqSchema) => void;
  usersById: Record<string, IUser>;
  isReadOnly?: boolean;
}

const MAX_TEXT_LENGTH = 300;

export const NoteCard = (props: NoteCardProps) => {
  const {
    note,
    unsavedNotes,
    handleNoteModified,
    createNote,
    updateNote,
    deleteNote,
    usersById,
    isReadOnly,
  } = props;

  const { userId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(
    () => !!unsavedNotes?.has(note.id),
  );
  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);

  const { expanded, toggle } = useShowMore(false);
  const needsToggle = note.note.length > MAX_TEXT_LENGTH;

  const handleFormClose = () => {
    setIsEditMode(false);
    handleNoteModified('CANCEL', note);
  };
  const handleUpdatePopup = () => {
    setIsEditMode(true);
  };

  const handleDeleteConfirmOpen = () => {
    setDeleteConfirmVisible(true);
  };
  const handleDeleteConfirmClose = () => {
    setDeleteConfirmVisible(false);
  };
  const clickYesDelete = async () => {
    setLoading(true);
    try {
      deleteNote(note);
    } catch (err) {
      handleApiErrorToast(err);
    } finally {
      setLoading(false);
      setDeleteConfirmVisible(false);
    }
  };

  const getUserAndDateForNote = (item: any) => {
    const user = usersById[item.createdBy];
    const date = (
      <DateDisplay
        as="span"
        date={item.modifyDate}
        timezoneConfigKey="userTimezone"
        textProps={{ fontSize: 'inherit', color: 'inherit', display: 'inline' }}
      />
    );
    return (
      <MText fontSize="sm" color="tGray.darkPurple">
        {user ? (
          <>
            {date} - {user.name}
          </>
        ) : (
          date
        )}
      </MText>
    );
  };

  const renderNoteActionMenu = (data: INoteResSchema) => (
    <MDropdownActions
      popOverContentProps={{ minW: '100px' }}
      renderTrigger={() => (
        <MFlex justify="space-between" p={1}>
          <MIconAction icon={MdMoreVert} />
        </MFlex>
      )}
    >
      <MDropdownActionItem onClick={() => handleUpdatePopup()}>
        <MText color="inherit">Edit</MText>
      </MDropdownActionItem>

      <MDropdownActionItem
        onClick={() => handleDeleteConfirmOpen()}
        color="tRed.base"
      >
        <MText color="inherit">Delete</MText>
      </MDropdownActionItem>
    </MDropdownActions>
  );
  const isUserNoteCreator = note.createdBy === userId;

  const renderShowMoreText = (text: string) => {
    return expanded || !needsToggle
      ? text
      : truncate(text, { length: MAX_TEXT_LENGTH });
  };

  return (
    <MCard variant="borderless">
      <Card.Body display="flex" flexDir="column" p={!isEditMode ? 4 : 0}>
        {deleteConfirmVisible && (
          <DeleteConfirmCard
            loading={loading}
            deleteNote={clickYesDelete}
            cancelDelete={handleDeleteConfirmClose}
          />
        )}
        {isEditMode && !deleteConfirmVisible && (
          <NoteForm
            initialNote={unsavedNotes?.get(note.id) || note}
            onClose={handleFormClose}
            createNote={createNote}
            updateNote={updateNote}
            handleNoteModified={handleNoteModified}
          />
        )}
        {!isEditMode && !deleteConfirmVisible && (
          <MBox>
            <Flex alignItems="center" justifyContent="space-between">
              {getUserAndDateForNote(note)}
              {!note.locked && !isReadOnly && isUserNoteCreator
                ? renderNoteActionMenu(note)
                : null}
            </Flex>

            <MText fontWeight="bold" fontSize="md" mt={3}>
              {note.subject}
            </MText>
            <MText mt="2" mb="2" fontSize="sm">
              {renderShowMoreText(note.note)}
            </MText>
            {needsToggle && (
              <Flex justifyContent="center">
                <MButton size="sm" variant="tertiary" onClick={toggle}>
                  {expanded ? 'Show Less' : 'Show More'}
                </MButton>
              </Flex>
            )}
          </MBox>
        )}
      </Card.Body>
    </MCard>
  );
};
